import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { formatDate } from '@angular/common';
import * as FileSaver from 'file-saver';
import { ContactListComponent } from './contact-list.component';
import { InvoiceModel } from '../../invoice/invoice/invoice.model';
import { InvoiceDialogPaidedComponent } from '../../invoice/invoice/invoice-dialog-paided.component';
import { InvoiceService } from '../../invoice/invoice/invoice.service';
import { ContactService } from './contact.service';
import { ContactMajorModel } from './ContactMajor.model';
import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { AddFileService } from '../../../shared/components/add-file/add-file.service';
import { LoadingDialogService } from '../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { StatusPillVariant } from '../../../shared/ui/status-pill/status-pill.component';

type ContactHistoryMode = 'client' | 'provider';
type ContactHistorySortKey = 'invoiceNumber' | 'name' | 'invoiceRelease' | 'total' | 'totalPaided';

interface ContactHistoryRow {
    invoice: InvoiceModel & { image?: boolean; pdf?: boolean };
    name: string;
    statusLabel: string;
    statusVariant: StatusPillVariant;
    searchText: string;
}

@Component({
    selector: 'convfact-contactt',
    templateUrl: './contact-dialog-t.component.html',
    styleUrls: ['./contact-dialog-t.component.css'],
    standalone: false
})
export class ContactDialogTComponent implements OnInit {
    uid: string = this.data.uid;
    clientAccount: string = this.data.clientAccount;
    providerAccount: string = this.data.providerAccount;

    loading = false;
    loadError = false;
    loaded = false;
    errorMessage = '';

    name: string;
    dataSourceClient: ContactMajorModel;
    dataSourceProvider: ContactMajorModel;

    paidedClient = 0;
    notPaidedClient = 0;
    noPaidedPreviousYearsClient = 0;
    totalClient = 0;
    paidedProvider = 0;
    notPaidedProvider = 0;
    noPaidedPreviousYearsProvider = 0;
    totalProvider = 0;

    mode: ContactHistoryMode = 'client';
    search = '';
    sortKey: ContactHistorySortKey | null = null;
    sortDir: 'asc' | 'desc' = 'asc';
    rows: ContactHistoryRow[] = [];

    static open(dialog: MatDialog, data: { uid: string; clientAccount?: string; providerAccount?: string }): Observable<void> {
        return dialog.open(ContactDialogTComponent, {
            width: '1100px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            autoFocus: false,
            data,
        }).afterClosed();
    }

    constructor(
        public dialogRef: MatDialogRef<ContactListComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public contactService: ContactService,
        public dialog: MatDialog,
        public invoiceService: InvoiceService,
        private addFileService: AddFileService,
        private loadingDialog: LoadingDialogService,
        private notify: NotificationService
    ) { }

    get client(): boolean {
        return this.mode === 'client';
    }

    get provider(): boolean {
        return this.mode === 'provider';
    }

    get clientCount(): number {
        return this.dataSourceClient?.invoices?.length || 0;
    }

    get providerCount(): number {
        return this.dataSourceProvider?.invoices?.length || 0;
    }

    get activeCount(): number {
        return this.client ? this.clientCount : this.providerCount;
    }

    get subtitle(): string {
        const parts = ['Histórico de facturas y gastos'];
        if (this.clientAccount) parts.push('Cta. cliente ' + this.clientAccount);
        if (this.providerAccount) parts.push('Cta. proveedor ' + this.providerAccount);
        return parts.join(' · ');
    }

    ngOnInit() {
        this.getData();
    }

    getData(): void {
        this.loading = true;
        this.loadError = false;
        this.errorMessage = '';

        Promise.all([
            this.contactService.getInvoicesByClient(this.uid),
            this.contactService.getExpensesByProvider(this.uid)
        ]).then(([clientResponse, providerResponse]) => {
            this.name = clientResponse?.receiverName ? clientResponse.receiverName : this.name;
            this.dataSourceClient = clientResponse;
            this.paidedClient = Utils.GetValueWithDecimals(clientResponse?.totalPaided || 0);
            this.notPaidedClient = Utils.GetValueWithDecimals(clientResponse?.totalNoPaided || 0);
            this.noPaidedPreviousYearsClient = Utils.GetValueWithDecimals(clientResponse?.totalNoPaidedPreviousYears || 0);
            this.totalClient = Utils.GetValueWithDecimals(this.paidedClient + this.notPaidedClient + this.noPaidedPreviousYearsClient);

            this.name = providerResponse?.receiverName ? providerResponse.receiverName : this.name;
            this.dataSourceProvider = providerResponse;
            this.paidedProvider = Utils.GetValueWithDecimals(providerResponse?.totalPaided || 0);
            this.notPaidedProvider = Utils.GetValueWithDecimals(providerResponse?.totalNoPaided || 0);
            this.noPaidedPreviousYearsProvider = Utils.GetValueWithDecimals(providerResponse?.totalNoPaidedPreviousYears || 0);
            this.totalProvider = Utils.GetValueWithDecimals(this.paidedProvider + this.notPaidedProvider + this.noPaidedPreviousYearsProvider);

            if (!this.loaded && this.clientCount === 0 && this.providerCount > 0) {
                this.mode = 'provider';
            }

            this.loaded = true;
            this.loading = false;
            this.refreshRows();
        }).catch(err => {
            this.loading = false;
            this.loadError = true;
            this.errorMessage = err?.error?.message || 'No se pudo cargar el histórico del contacto';
            this.notify.error(this.errorMessage);
        });
    }

    changeClient(): void {
        this.setMode('client');
    }

    changeProvider(): void {
        this.setMode('provider');
    }

    onSearchChange(value: string): void {
        this.search = value || '';
        this.refreshRows();
    }

    toggleSort(key: ContactHistorySortKey): void {
        if (this.sortKey !== key) {
            this.sortKey = key;
            this.sortDir = 'asc';
        } else if (this.sortDir === 'asc') {
            this.sortDir = 'desc';
        } else {
            this.sortKey = null;
        }
        this.refreshRows();
    }

    sortIcon(key: ContactHistorySortKey): string {
        if (this.sortKey !== key) return '';
        return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
    }

    getExcel(): void {
        const source = this.client ? this.dataSourceClient : this.dataSourceProvider;
        const fileName = this.client
            ? Utils.GetExcelName('Contacto_T_cliente_' + this.name)
            : Utils.GetExcelName('Contacto_T_proveedor_' + this.name);

        this.loadingDialog.run('Generando Excel…', this.contactService.getExcelT(source))
            .then(response => {
                FileSaver.saveAs(response, fileName);
                this.notify.ok('Excel generado correctamente');
            })
            .catch(() => this.notify.error('No se pudo generar el Excel'));
    }

    invoicePaided(uid: string): void {
        const data = this.client
            ? { uid: uid, isInvoice: true, isTicket: false, isIncome: true }
            : { uid: uid, isInvoice: true, isTicket: false };

        InvoiceDialogPaidedComponent.open(this.dialog, data).subscribe(() => this.getData());
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    downloadFileImage(uid: string): void {
        this.loadingDialog.run('Descargando documento…', this.addFileService.downloadFileImage(uid, consts.INVOICE_ENTITY, consts.EMPTY_GUID))
            .then(response => FileSaver.saveAs(response, Utils.GetFileName('Gasto', consts.EXT_PNG)))
            .catch(() => this.notify.error('No se pudo descargar el documento'));
    }

    downloadFilePdf(uid: string): void {
        this.loadingDialog.run('Descargando documento…', this.addFileService.downloadFilePDF(uid, consts.INVOICE_ENTITY, consts.EMPTY_GUID))
            .then(response => FileSaver.saveAs(response, Utils.GetPdfName('Gasto')))
            .catch(() => this.notify.error('No se pudo descargar el documento'));
    }

    pdfClient(uid: string, invoiceNumber: string): void {
        this.loadingDialog.run('Generando PDF…', this.invoiceService.pdf(uid, consts.EMPTY_GUID))
            .then(response => FileSaver.saveAs(response, Utils.GetPdfName('Ingreso_' + invoiceNumber)))
            .catch(() => this.notify.error('No se pudo descargar el PDF'));
    }

    trackByUid(_: number, row: ContactHistoryRow): string {
        return row.invoice.uid;
    }

    private setMode(mode: ContactHistoryMode): void {
        if (this.mode === mode) return;
        this.mode = mode;
        this.refreshRows();
    }

    private refreshRows(): void {
        const source = this.client ? this.dataSourceClient : this.dataSourceProvider;
        const invoices = (source?.invoices || []) as ContactHistoryRow['invoice'][];
        const term = this.normalize(this.search.trim());

        let rows = invoices.map(invoice => this.toRow(invoice));

        if (term) {
            rows = rows.filter(row => row.searchText.includes(term));
        }

        if (this.sortKey) {
            const key = this.sortKey;
            const factor = this.sortDir === 'asc' ? 1 : -1;
            rows = [...rows].sort((a, b) => this.compare(this.sortValue(a, key), this.sortValue(b, key)) * factor);
        }

        this.rows = rows;
    }

    private toRow(invoice: ContactHistoryRow['invoice']): ContactHistoryRow {
        const name = (this.client ? invoice.receiverName : invoice.issuerName) || '';
        const status = this.status(invoice);
        const date = invoice.invoiceRelease ? formatDate(invoice.invoiceRelease, 'dd/MM/yyyy', 'es-ES') : '';
        const searchText = this.normalize([
            invoice.invoiceNumber, name, date, invoice.total, invoice.totalPaided, status.label
        ].filter(v => v != null && v !== '').join(' '));

        return { invoice, name, statusLabel: status.label, statusVariant: status.variant, searchText };
    }

    private status(invoice: InvoiceModel): { label: string; variant: StatusPillVariant } {
        if (invoice.paided != null) {
            return { label: this.client ? 'Cobrada' : 'Pagada', variant: 'success' };
        }
        if (invoice.rectifying) {
            return { label: 'Rectificativa', variant: 'info' };
        }
        if ((invoice.totalPaided || 0) > 0) {
            return { label: 'Parcial', variant: 'info' };
        }
        return { label: 'Pendiente', variant: 'warning' };
    }

    private sortValue(row: ContactHistoryRow, key: ContactHistorySortKey): string | number {
        switch (key) {
            case 'name': return this.normalize(row.name);
            case 'invoiceNumber': return this.normalize(row.invoice.invoiceNumber || '');
            case 'invoiceRelease': return row.invoice.invoiceRelease ? new Date(row.invoice.invoiceRelease).getTime() : 0;
            case 'total': return row.invoice.total || 0;
            default: return row.invoice.totalPaided || 0;
        }
    }

    private compare(a: string | number, b: string | number): number {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b), 'es', { numeric: true });
    }

    private normalize(value: string): string {
        return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }
}
